import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { groupsActions, sessionActions } from '../../../store';
import { createWithURLSearchParams } from '../../../fetch';
import { useTranslation } from '../../../common/LocalizationProvider';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: true
    },
    validationSchema: LoginSchema,
    onSubmit: async (values) => {
      const response = await createWithURLSearchParams(
        'session',
        `email=${values.email}&password=${values.password}`
      );

      if (response.ok) {
        window.localStorage.clear();

        const result = await response.json();
        dispatch(sessionActions.update(result));
        dispatch(sessionActions.authenticated(true));

        const groupId =
          JSON.parse(window.localStorage.getItem('groupId')) ||
          JSON.parse(window.localStorage.getItem('groupId')) === 0
            ? JSON.parse(window.localStorage.getItem('groupId'))
            : result.permission[0].groupid || 0;

        const permission = result.permission.find((item) => item.groupid === groupId) || {};

        if (!result.administrator) {
          dispatch(groupsActions.select(groupId));
        } else {
          dispatch(groupsActions.select(0));
        }

        if (permission && permission.status < 3) {
          navigate('/live/map', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        setSubmitting(false);
        setErrors({ email: 'Invalid', password: 'Invalid' });
      }
    }
  });

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    setErrors
  } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Stack spacing={3} width="100%">
        <TextField
          fullWidth
          autoComplete="email"
          type="email"
          label={t('fields_Email')}
          {...getFieldProps('email')}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email && errors.email}
        />

        <TextField
          fullWidth
          autoComplete="password"
          type={showPassword ? 'text' : 'password'}
          label={t('fields_Password')}
          {...getFieldProps('password')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleShowPassword} edge="end">
                  <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                </IconButton>
              </InputAdornment>
            )
          }}
          error={Boolean(touched.password && errors.password)}
          helperText={touched.password && errors.password}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        sx={{ my: 2 }}
      >
        <FormControlLabel
          control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
          label={t('login_RememberMe')}
        />

        <Link component={RouterLink} variant="subtitle2" to="/forgot/password">
          {t('login_ForgotPassword')}
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        onClick={handleSubmit}
      >
        {t('login_Button')}
      </LoadingButton>
    </FormikProvider>
  );
}
